/**
 * Blog Post Editor - Create and edit blog posts
 * Note: This is a basic version. Rich text editor will be added next.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  HStack,
  VStack,
  Switch,
  FormHelperText,
  Grid,
  GridItem,
  Text,
  Badge,
  useToast,
  InputGroup,
  InputLeftAddon
} from '@chakra-ui/react';
import {
  getBlogPostById,
  createBlogPost,
  updateBlogPost,
  getBlogCategories,
  generateSlug,
  isSlugUnique,
  type BlogPost,
  type BlogCategory
} from '../../services/blogService';
import { Timestamp } from 'firebase/firestore';

interface BlogPostEditorProps {
  postId: string | null;
  onSave: () => void;
  onCancel: () => void;
}

const BlogPostEditor = ({ postId, onSave, onCancel }: BlogPostEditorProps) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featuredImage: '',
    category: '',
    tags: '',
    metaTitle: '',
    metaDescription: '',
    isPublished: false,
    author: 'Glisten Team', // TODO: Get from auth
    authorId: 'admin' // TODO: Get from auth
  });
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const toast = useToast();

  useEffect(() => {
    loadCategories();
    if (postId) {
      loadPost(postId);
    }
  }, [postId]);

  const loadCategories = async () => {
    try {
      const data = await getBlogCategories();
      setCategories(data);
      if (data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: data[0].name }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPost = async (id: string) => {
    try {
      setLoading(true);
      const post = await getBlogPostById(id);
      if (post) {
        setFormData({
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt,
          content: post.content,
          featuredImage: post.featuredImage || '',
          category: post.category,
          tags: post.tags.join(', '),
          metaTitle: post.metaTitle || '',
          metaDescription: post.metaDescription || '',
          isPublished: post.isPublished,
          author: post.author,
          authorId: post.authorId
        });
        setSlugManuallyEdited(true);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      toast({
        title: 'Error',
        description: 'Failed to load post',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-generate slug from title
    if (field === 'title' && !slugManuallyEdited) {
      setFormData(prev => ({ ...prev, slug: generateSlug(value) }));
    }

    // Auto-generate meta title from title
    if (field === 'title' && !formData.metaTitle) {
      setFormData(prev => ({ 
        ...prev, 
        metaTitle: `${value} | Glisten Eco-Shine` 
      }));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setFormData(prev => ({ ...prev, slug: generateSlug(value) }));
  };

  const validateForm = (): string | null => {
    if (!formData.title.trim()) return 'Title is required';
    if (!formData.slug.trim()) return 'Slug is required';
    if (!formData.excerpt.trim()) return 'Excerpt is required';
    if (!formData.content.trim()) return 'Content is required';
    if (!formData.category) return 'Category is required';
    if (formData.excerpt.length > 300) return 'Excerpt must be under 300 characters';
    return null;
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      toast({
        title: 'Validation Error',
        description: error,
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Check slug uniqueness
    const slugUnique = await isSlugUnique(formData.slug, postId || undefined);
    if (!slugUnique) {
      toast({
        title: 'Error',
        description: 'This slug is already in use. Please choose a different one.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);

      const postData: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt'> = {
        title: formData.title.trim(),
        slug: formData.slug.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content.trim(),
        featuredImage: formData.featuredImage.trim(),
        category: formData.category,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        metaTitle: formData.metaTitle.trim() || `${formData.title} | Glisten Eco-Shine`,
        metaDescription: formData.metaDescription.trim() || formData.excerpt.trim(),
        isPublished: formData.isPublished,
        publishedAt: formData.isPublished ? Timestamp.now() : undefined,
        author: formData.author,
        authorId: formData.authorId
      };

      if (postId) {
        await updateBlogPost(postId, postData);
      } else {
        await createBlogPost(postData);
      }

      toast({
        title: 'Success',
        description: `Post ${postId ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 2000,
      });

      onSave();
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        title: 'Error',
        description: 'Failed to save post',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" borderWidth="1px">
      <VStack spacing={6} align="stretch">
        {/* Title & Slug */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter post title"
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Slug (URL)</FormLabel>
              <InputGroup>
                <InputLeftAddon>/blog/</InputLeftAddon>
                <Input
                  value={formData.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="url-friendly-slug"
                />
              </InputGroup>
              <FormHelperText>
                Will be: /blog/{formData.slug || 'your-slug'}
              </FormHelperText>
            </FormControl>
          </GridItem>
        </Grid>

        {/* Excerpt */}
        <FormControl isRequired>
          <FormLabel>Excerpt</FormLabel>
          <Textarea
            value={formData.excerpt}
            onChange={(e) => handleChange('excerpt', e.target.value)}
            placeholder="Brief description for listings and SEO (under 300 chars)"
            rows={3}
            maxLength={300}
          />
          <FormHelperText>
            {formData.excerpt.length}/300 characters
          </FormHelperText>
        </FormControl>

        {/* Content */}
        <FormControl isRequired>
          <FormLabel>Content</FormLabel>
          <Textarea
            value={formData.content}
            onChange={(e) => handleChange('content', e.target.value)}
            placeholder="Write your blog post content here... (Rich text editor coming soon!)"
            rows={15}
            fontFamily="mono"
          />
          <FormHelperText>
            Rich text editor will be added in next step. For now, you can use HTML.
          </FormHelperText>
        </FormControl>

        {/* Featured Image */}
        <FormControl>
          <FormLabel>Featured Image URL</FormLabel>
          <Input
            value={formData.featuredImage}
            onChange={(e) => handleChange('featuredImage', e.target.value)}
            placeholder="https://your-cdn.com/image.jpg"
          />
          <FormHelperText>
            Image upload feature coming next!
          </FormHelperText>
        </FormControl>

        {/* Category & Tags */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Category</FormLabel>
              <Select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                {categories.length === 0 ? (
                  <option value="">No categories yet</option>
                ) : (
                  categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))
                )}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>Tags</FormLabel>
              <Input
                value={formData.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
                placeholder="eco-friendly, tips, maintenance"
              />
              <FormHelperText>Comma-separated tags</FormHelperText>
            </FormControl>
          </GridItem>
        </Grid>

        {/* SEO Meta */}
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontWeight="bold" mb={3}>SEO Settings</Text>
          
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Meta Title</FormLabel>
              <Input
                value={formData.metaTitle}
                onChange={(e) => handleChange('metaTitle', e.target.value)}
                placeholder="Auto-generated from title"
                maxLength={60}
              />
              <FormHelperText>{formData.metaTitle.length}/60 characters</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Meta Description</FormLabel>
              <Textarea
                value={formData.metaDescription}
                onChange={(e) => handleChange('metaDescription', e.target.value)}
                placeholder="Auto-generated from excerpt"
                rows={3}
                maxLength={160}
              />
              <FormHelperText>{formData.metaDescription.length}/160 characters</FormHelperText>
            </FormControl>
          </VStack>
        </Box>

        {/* Publish Status */}
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="publish" mb="0">
            Publish Status
          </FormLabel>
          <Switch
            id="publish"
            isChecked={formData.isPublished}
            onChange={(e) => handleChange('isPublished', e.target.checked)}
            colorScheme="green"
          />
          <Badge
            ml={3}
            colorScheme={formData.isPublished ? 'green' : 'orange'}
          >
            {formData.isPublished ? 'Published' : 'Draft'}
          </Badge>
        </FormControl>

        {/* Actions */}
        <HStack spacing={3} pt={4} borderTopWidth="1px">
          <Button
            colorScheme="teal"
            onClick={handleSave}
            isLoading={loading}
          >
            {postId ? 'Update Post' : 'Create Post'}
          </Button>
          <Button
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          {formData.isPublished && formData.slug && (
            <Button
              variant="ghost"
              as="a"
              href={`https://www.glisten-ecoshine.com/blog/${formData.slug}`}
              target="_blank"
            >
              Preview Live
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};

export default BlogPostEditor;
